import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { FEATURED } from "../data/projects";

/**
 * App-Slides showcase — vertical stacked, fade-up + scale reveal on scroll.
 * Each slide fills most of the viewport width and reveals cinematically.
 */
export default function Featured() {
  return (
    <section
      id="featured"
      data-testid="featured-section"
      className="relative py-32 md:py-48"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1 }}
          className="mb-14 md:mb-20"
        >
          <div className="flex items-center mb-4">
            <span className="h-rule" />
            <span className="overline">03 — App Slides</span>
          </div>
          <h2
            dir="rtl"
            className="font-arabic text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.2] text-white"
          >
            تصاميم سلايدات <span className="text-[#FD1843] glow-text">للتطبيقات</span>
          </h2>
        </motion.div>

        <div className="space-y-24 md:space-y-32">
          {FEATURED.map((f, i) => (
            <FeaturedItem key={f.id} item={f} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function FeaturedItem({ item, index }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-120px" });

  return (
    <motion.article
      ref={ref}
      initial={{ opacity: 0, y: 80, scale: 0.94 }}
      animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
      transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
      className="relative"
      data-testid={`featured-item-${item.id}`}
    >
      {/* Image showcase */}
      <div className="relative w-full aspect-[16/9] md:aspect-[21/9] overflow-hidden rounded-sm group bg-[#1c1c1c]">
        <motion.img
          src={item.image}
          alt={item.title || item.accent}
          initial={{ scale: 1.15 }}
          animate={inView ? { scale: 1 } : {}}
          transition={{ duration: 1.8, ease: [0.22, 1, 0.36, 1] }}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#171717]/85 via-[#171717]/10 to-transparent" />
        <div
          className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-700"
          style={{ boxShadow: "inset 0 0 80px rgba(253,24,67,0.18)" }}
        />

        {/* Arabic overline pill */}
        <div
          dir="rtl"
          className="absolute top-6 right-6 md:top-8 md:right-8 font-arabic font-bold text-sm md:text-base text-[#FD1843] bg-black/50 backdrop-blur-md px-4 py-2 rounded-full flex items-center gap-2"
        >
          <span className="font-mono text-[10px] tracking-[0.28em] text-white/50">
            {String(index + 1).padStart(2, "0")}
          </span>
          <span className="w-1 h-1 rounded-full bg-white/30" />
          <span>{item.accent}</span>
        </div>

        {/* Massive title overlay (hidden when title is empty) */}
        {item.title ? (
          <motion.h3
            initial={{ y: 40, opacity: 0 }}
            animate={inView ? { y: 0, opacity: 1 } : {}}
            transition={{ delay: 0.35, duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="absolute bottom-6 left-6 right-6 md:bottom-10 md:left-10 md:right-10 font-display font-bold text-white uppercase tracking-[-0.045em] leading-[0.9] text-[10vw] md:text-[7vw] lg:text-[5.5vw]"
          >
            {item.title}
          </motion.h3>
        ) : null}
      </div>

      {/* Title (if any) shown below image */}
      {item.title ? (
        <motion.h3
          initial={{ y: 24, opacity: 0 }}
          animate={inView ? { y: 0, opacity: 1 } : {}}
          transition={{ delay: 0.3, duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="mt-6 md:mt-8 font-display font-bold text-white uppercase tracking-[-0.045em] leading-[0.95] text-[8vw] md:text-[5vw] lg:text-[4vw]"
        >
          {item.title}
        </motion.h3>
      ) : null}

      {/* Description row */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ delay: 0.55, duration: 1 }}
        dir="rtl"
        className="mt-8 md:mt-10 max-w-3xl"
      >
        <div className="font-mono text-[10px] tracking-[0.28em] text-white/40 uppercase mb-3" dir="ltr">
          Featured Slide
        </div>
        <p className="font-arabic text-white/85 text-lg md:text-xl font-medium leading-[1.95]">
          {item.description}
        </p>
      </motion.div>
    </motion.article>
  );
}
